import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { UserService } from '../../core/services/user.service';
import { EventService } from '../../core/services/event.service';
import { ImpersonationService } from '../../core/services/impersonation.service';
import { NotificationService } from '../../core/services/notification.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { User } from '../../core/interfaces/user';
import { Hotel } from '../../core/interfaces/hotel';
import { ComponentsModule } from '../../components/components.module';
import { passwordValidator } from '../../core/validators/password.validator';
import { TrustedDevicesService, TrustedDevice } from '../../core/services/trusted-devices.service';
import { TwoFactorSettingsComponent } from './two-factor-settings/two-factor-settings.component';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule, ComponentsModule, TwoFactorSettingsComponent]
})
export class UserProfileComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  breadCrumbItems: { label: string; active?: boolean }[] = [];
  user: User | null = null;
  assignedHotels: Hotel[] = [];

  // Forms
  profileForm!: FormGroup;
  passwordForm!: FormGroup;

  // State
  isLoadingProfile = false;
  isSavingProfile = false;
  isChangingPassword = false;
  profileSubmitted = false;
  passwordSubmitted = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  // Two-factor authentication. The section itself lives in
  // app-two-factor-settings; this flag only gates the trusted-device list.
  isTwoFactorEnabled = false;
  private originalProfile: { firstName: string; lastName: string } = { firstName: '', lastName: '' };

  // Trusted Devices Management
  trustedDevices: TrustedDevice[] = [];
  isLoadingDevices = false;
  showRevokeDeviceModal = false;
  deviceToRevoke: TrustedDevice | null = null;
  isRevokingDevice = false;
  revokeDeviceError = '';

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private eventService: EventService,
    private notificationService: NotificationService,
    private confirmationService: ConfirmationService,
    private trustedDevicesService: TrustedDevicesService,
    private translate: TranslateService,
    private impersonationService: ImpersonationService
  ) {}

  ngOnInit(): void {
    this.initializeBreadcrumbs();
    this.initializeForms();
    this.loadUserProfile();
  }

  private initializeBreadcrumbs(): void {
    this.breadCrumbItems = [
      { label: this.translate.instant('profile.my_profile'), active: true }
    ];
  }

  private initializeForms(): void {
    // Profile form
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(30)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(30)]]
    });

    // Password form
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  private loadUserProfile(): void {
    this.isLoadingProfile = true;
    this.eventService.broadcast('globalLoader:show', true);

    // Get user from service
    this.user = this.userService.getUser();

    if (this.user) {
      // Extract first and last name
      let firstName = this.user.profile?.first_name || '';
      let lastName = this.user.profile?.last_name || '';

      // If profile names are empty, parse from the name field
      if (!firstName && !lastName && this.user.name) {
        const nameParts = this.user.name.trim().split(/\s+/);
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // Populate profile form
      this.profileForm.patchValue({
        firstName,
        lastName
      });

      this.originalProfile = { firstName, lastName };

      // Get assigned hotels
      if (Array.isArray(this.user.hotels)) {
        this.assignedHotels = this.user.hotels as Hotel[];
      }

      // Seed from the cached user; app-two-factor-settings confirms it with
      // 2fa/status and reports back through onTwoFactorStatusChange().
      this.isTwoFactorEnabled = !!this.user.two_factor_confirmed_at;
    }

    this.isLoadingProfile = false;
    this.eventService.broadcast('globalLoader:hide', false);
  }

  // Form getters for template
  get f(): { [key: string]: AbstractControl } { return this.profileForm.controls; }
  get pf(): { [key: string]: AbstractControl } { return this.passwordForm.controls; }

  private passwordMatchValidator(group: FormGroup): ValidationErrors | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      return { mismatch: true };
    }
    return null;
  }

  // Save profile
  saveProfile(): void {
    this.profileSubmitted = true;
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isSavingProfile = true;
    this.eventService.broadcast('globalLoader:show', true);

    const profileData = {
      first_name: this.profileForm.value.firstName,
      last_name: this.profileForm.value.lastName
    };

    this.userService.updateProfile(profileData)
      .pipe(
        finalize(() => {
          this.isSavingProfile = false;
          this.eventService.broadcast('globalLoader:hide', false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.confirmationService.swalSuccess(this.translate.instant('profile.profile_updated'));

          // Update local user data so the Account information section reflects
          // the change immediately without waiting for the API re-fetch.
          if (this.user) {
            if (this.user.profile) {
              this.user.profile.first_name = profileData.first_name;
              this.user.profile.last_name = profileData.last_name;
            }
            // user.name takes priority over profile names in the template,
            // so it must be updated too or the displayed name won't change.
            this.user.name = `${profileData.first_name} ${profileData.last_name}`.trim();
          }

          // Update originalProfile so Cancel after a successful save
          // does not revert to the pre-save values.
          this.originalProfile = {
            firstName: profileData.first_name,
            lastName: profileData.last_name
          };

          // Re-fetch the user from the API so the cookie is updated and the
          // 'userChanged' event is broadcast — the topbar listens to that event
          // and updates the displayed name without requiring a page refresh.
          this.userService.updateUser();

          // When an admin is viewing the app as this hotel manager (impersonation),
          // the topbar shows impersonationState.targetUserName rather than user.name.
          // That value is set once when impersonation starts, so it must be updated
          // here or the header will keep showing the original pre-save name.
          const newName = `${profileData.first_name} ${profileData.last_name}`.trim();
          this.impersonationService.updateTargetUserName(newName);
        },
        error: (error) => {
          const errorMessage = error?.message || this.translate.instant('profile.profile_update_failed');
          this.confirmationService.swalError(errorMessage);
        }
      });
  }

  // Change password
  changePassword(): void {
    this.passwordSubmitted = true;
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isChangingPassword = true;
    this.eventService.broadcast('globalLoader:show', true);

    const passwordData = {
      current_password: this.passwordForm.value.currentPassword,
      new_password: this.passwordForm.value.newPassword,
      new_password_confirmation: this.passwordForm.value.confirmPassword
    };

    this.userService.changePassword(passwordData)
      .pipe(
        finalize(() => {
          this.isChangingPassword = false;
          this.eventService.broadcast('globalLoader:hide', false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.passwordSubmitted = false;
          this.confirmationService.swalSuccess(this.translate.instant('profile.password_changed'));
          this.passwordForm.reset();
        },
        error: (error) => {
          // Error interceptor transforms errors to { status, message, errors }
          const errorMessage = error?.message
            || this.translate.instant('profile.current_password_incorrect');
          this.confirmationService.swalError(errorMessage);
        }
      });
  }

  // Toggle password visibility
  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  // Get user role display name
  getUserRole(): string {
    if (this.user?.internal_role) {
      const roleNames: Record<string, string> = {
        'super-admin': 'Super Admin',
        'customer-success': 'Customer Success',
        'viewer-system': 'Viewer'
      };
      return roleNames[this.user.internal_role] || this.user.internal_role;
    }
    return this.translate.instant('profile.role_user');
  }

  // Format last login date as DD.MM.YYYY at HH.MM
  getLastLogin(): string {
    if (!this.user?.last_login) {
      return this.translate.instant('profile.never');
    }
    const date = new Date(this.user.last_login);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${this.translate.instant('profile.at')} ${hours}.${minutes}`;
  }

  /**
   * Called by app-two-factor-settings after 2fa/status loads and after any
   * change there. Trusted devices only exist while 2FA is on, and the backend
   * revokes them on remove, disable and first setup, so reload them here.
   */
  onTwoFactorStatusChange(enabled: boolean): void {
    this.isTwoFactorEnabled = enabled;
    this.loadTrustedDevices();
  }

  private showLoader(): void {
    this.eventService.broadcast('globalLoader:show', true);
  }

  private hideLoader(): void {
    this.eventService.broadcast('globalLoader:hide', false);
  }

  onCancel(): void {
    const hasChanges = this.profileForm.dirty;

    this.profileSubmitted = false;
    this.profileForm.patchValue({
      firstName: this.originalProfile.firstName,
      lastName: this.originalProfile.lastName
    });
    this.profileForm.markAsPristine();
    this.profileForm.markAsUntouched();

    if (hasChanges) {
      this.showLoader();
      setTimeout(() => {
        this.hideLoader();
      }, 1000);
    }
  }

  /**
   * Load trusted devices for the user
   */
  loadTrustedDevices(): void {
    if (!this.isTwoFactorEnabled) {
      // Only load devices if 2FA is enabled
      this.trustedDevices = [];
      return;
    }

    this.isLoadingDevices = true;

    this.trustedDevicesService.getTrustedDevices()
      .pipe(
        finalize(() => {
          this.isLoadingDevices = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (response.data?.devices) {
            this.trustedDevices = response.data.devices;
          } else {
            this.trustedDevices = [];
          }
        },
        error: (error) => {
          console.error('Failed to load trusted devices:', error);
          this.trustedDevices = [];
        }
      });
  }

  /**
   * Open the revoke device confirmation modal
   */
  openRevokeDeviceModal(device: TrustedDevice): void {
    this.deviceToRevoke = device;
    this.showRevokeDeviceModal = true;
    this.revokeDeviceError = '';
  }

  /**
   * Close the revoke device modal
   */
  closeRevokeDeviceModal(): void {
    this.showRevokeDeviceModal = false;
    this.deviceToRevoke = null;
    this.revokeDeviceError = '';
  }

  /**
   * Confirm and revoke a specific trusted device
   */
  confirmRevokeDevice(): void {
    if (!this.deviceToRevoke) {
      return;
    }

    this.isRevokingDevice = true;
    this.revokeDeviceError = '';

    this.trustedDevicesService.revokeDevice(this.deviceToRevoke.id)
      .pipe(
        finalize(() => {
          this.isRevokingDevice = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.confirmationService.swalSuccess(
            this.translate.instant('profile.trusted_devices.device_revoked')
          );
          this.closeRevokeDeviceModal();
          this.loadTrustedDevices(); // Reload the list
        },
        error: (error) => {
          this.revokeDeviceError = error?.message
            || this.translate.instant('profile.trusted_devices.revoke_failed');
        }
      });
  }

  /**
   * Revoke all trusted devices with confirmation
   */
  revokeAllDevices(): void {
    const title = this.translate.instant('profile.trusted_devices.revoke_all_title');
    const message = this.translate.instant('profile.trusted_devices.revoke_all_message');
    const confirmText = this.translate.instant('profile.trusted_devices.revoke_all_confirm');
    const cancelText = this.translate.instant('common.cancel');

    this.confirmationService.swalConfirm({ title, message, confirmText, cancelText })
      .then((confirmed) => {
        if (confirmed) {
          this.executeRevokeAllDevices();
        }
      });
  }

  /**
   * Execute revoke all devices
   */
  private executeRevokeAllDevices(): void {
    this.eventService.broadcast('globalLoader:show', true);

    this.trustedDevicesService.revokeAllDevices()
      .pipe(
        finalize(() => {
          this.eventService.broadcast('globalLoader:hide', false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.confirmationService.swalSuccess(
            this.translate.instant('profile.trusted_devices.all_devices_revoked')
          );
          this.loadTrustedDevices(); // Reload the list (will be empty)
        },
        error: (error) => {
          const errorMessage = error?.message
            || this.translate.instant('profile.trusted_devices.revoke_all_failed');
          this.confirmationService.swalError(errorMessage);
        }
      });
  }

  /**
   * Format date for display (DD.MM.YYYY at HH:MM)
   */
  formatDeviceDate(dateString: string): string {
    if (!dateString) {
      return '-';
    }

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${this.translate.instant('profile.at')} ${hours}:${minutes}`;
  }

  /**
   * Get relative time string (e.g., "2 days ago", "Just now")
   */
  getRelativeTime(dateString: string): string {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return this.translate.instant('profile.trusted_devices.just_now');
    } else if (diffMins < 60) {
      return this.translate.instant('profile.trusted_devices.minutes_ago', { minutes: diffMins });
    } else if (diffHours < 24) {
      return this.translate.instant('profile.trusted_devices.hours_ago', { hours: diffHours });
    } else if (diffDays === 1) {
      return this.translate.instant('profile.trusted_devices.yesterday');
    } else if (diffDays < 30) {
      return this.translate.instant('profile.trusted_devices.days_ago', { days: diffDays });
    } else {
      return this.formatDeviceDate(dateString);
    }
  }

  /**
   * Get days until expiration
   */
  getDaysUntilExpiry(expiresAt: string): number {
    if (!expiresAt) {
      return 0;
    }

    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / 86400000);

    return Math.max(0, diffDays);
  }

  /**
   * Check if device is expiring soon (within 7 days)
   */
  isExpiringSoon(expiresAt: string): boolean {
    return this.getDaysUntilExpiry(expiresAt) <= 7;
  }

  /**
   * Get device icon class based on device name
   */
  getDeviceIcon(deviceName: string): string {
    const name = deviceName.toLowerCase();

    if (name.includes('chrome') || name.includes('firefox') || name.includes('safari') || name.includes('edge')) {
      return 'globe';
    } else if (name.includes('ios') || name.includes('iphone') || name.includes('ipad') || name.includes('android')) {
      return 'smartphone';
    } else if (name.includes('windows') || name.includes('mac') || name.includes('macos') || name.includes('linux')) {
      return 'monitor';
    } else {
      return 'laptop';
    }
  }

}
